import { ConnectedDeviceEnum, DeviceState, DeviceType, PanelType } from '@web/ui-mobile';
import { BatteryState } from 'libs/ui-mobile/src/lib/models/enums/battery-state.enum';
import { WorkingMode } from 'libs/ui-mobile/src/lib/models/enums/working-mode.enum';
import { ElectricalDataKeys, StreamMeterFunction, StreamMeterMode } from './enum/stream-meter.enum';

export type BaseConnectedDevice = {
    id?: string;
    name?: string;
    serialNumber?: string;
    type: DeviceType;
    lastSynchronizationDate?: string;
    state?: boolean;
};

export type SolarPanel = BaseConnectedDevice & {
    type: PanelType;
    firmwareVersion?: string;
    batteryPreserveEnergy?: boolean;
    batteryThreshold?: number;
    wifiName?: string;
    rssiWifi?: number;
};

export type SolarPanelPatch = Omit<SolarPanel, 'wifiName' | 'type'>;

export enum FlowWorkingMode {
    POWER_OPTIM = 'POWER_OPTIM',
    COST_OPTIM = 'COST_OPTIM',
}

export type Hub = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.STREAM_CONNECT;
    hostname?: string;
    wifiName?: string;
    rssiWifi?: number;
    connectedDevices?: BaseConnectedDevice[];
    firmwareVersion?: string;
    flowWorkingMode?: FlowWorkingMode;
};
export type HubPatch = Omit<Hub, 'type' | 'firmwareVersion'>;

export type ErlDevice = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.ERL | ConnectedDeviceEnum.ERL_GEN2;
    firmwareVersion?: string;
    state?: DeviceState;
    erlType?: DeviceType;
    isSellBack?: boolean;
};

export type ERL = ErlDevice & {
    type: ConnectedDeviceEnum.ERL;
};

export type ERL_GEN2 = ErlDevice & {
    type: ConnectedDeviceEnum.ERL_GEN2;
};

export type StreamMeter = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.STREAM_METER;
    firmwareVersion?: string;
    rssi?: number;
    electricalSystem?: StreamMeterMode;
    phasesConfiguration?: { [key in ElectricalDataKeys]: { phaseFunction: StreamMeterFunction } };
    pairingDate?: string;
};
export type StreamMeterLight = Omit<
    StreamMeter,
    'firmwareVersion' | 'lastSynchronizedDate' | 'rssi' | 'status' | 'electricalSystem' | 'phasesConfiguration'
>;

export type StreamMeterPost = Omit<StreamMeter, 'phasesConfiguration'> & {
    phasesConfiguration?: { [key in ElectricalDataKeys]: StreamMeterFunction };
};

export type PhaseConfiguration = {
    phasePurpose: StreamMeterFunction;
};

export type StorageBattery = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.STOREY;
    wifiName?: string;
    rssiWifi?: number;
    firmwareVersion?: string;
    connectedPacks?: number;
    batteryState?: BatteryState;
    workingMode?: WorkingMode;
    minSoc?: number;
    inhibitionTimeout?: Date;
    suncastManualConfigurations?: {
        priceName: string;
        priceAmount: number;
        batteryBehavior: 'CHARGE' | 'DISCHARGE';
        power: number;
    }[];
};
export type StorageBatteryPatch = Omit<StorageBattery, 'type' | 'firmwareVersion' | 'wifiName' | 'serialNumber'>;

export type ConnectedDevice = SolarPanel | Hub | StorageBattery | ERL | ERL_GEN2 | StreamMeter | StreamMeterLight;
export type ConnectedDevicePost = SolarPanel | Hub | StorageBattery | ERL | ERL_GEN2 | StreamMeterPost;
